import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

import { takeUntil } from 'rxjs/operators';

import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ShIconService } from '../../services/icon.service';
import { IconSize } from './entities/icon-sizes.entity';

@Component({
    selector: 'svg[sh-icon]',
    templateUrl: './icon.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true
})
export class ShIconComponent extends BaseContainer implements OnInit, OnDestroy {
    iconSize: IconSize = IconSize.REGULAR;
    shouldAddClasses = true;

    @Input()
    set size(size: IconSize) {
        this.iconSize = size;
        this.setIconSizeClass();
    }

    @Input()
    set name(name: string) {
        if (name) {
            this.fetchIcon(name);
            this.setIconAriaLabel(name);
        }
    }

    @Input()
    set addDefaultClasses(shouldAdd: boolean) {
        this.shouldAddClasses = shouldAdd;
        this.setIconSizeClass();
    }

    constructor(
        private readonly element: ElementRef,
        private readonly renderer: Renderer2,
        private readonly iconService: ShIconService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setIconSizeClass();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setIconSizeClass(): void {
        const element: SVGElement = this.element.nativeElement;

        this.removeAllDefaultClasses(element);

        if (this.shouldAddClasses) {
            this.renderer.addClass(element, this.getIconSizeClass());
        }
    }

    private fetchIcon(name: string): void {
        this.iconService.get$(name).pipe(takeUntil(this.destroy$)).subscribe(this.setIcon.bind(this));
    }

    private setIconAriaLabel(name: string): void {
        const element: SVGElement = this.element.nativeElement;
        this.renderer.setAttribute(element, 'data-icon-label', name);
    }

    private setIcon(svg: SVGElement): void {
        const newIconElement: SVGElement = svg.cloneNode(true) as SVGElement;
        const svgElement: SVGElement = this.element.nativeElement;
        const newChildNodes: NodeList = newIconElement.childNodes;
        const currentChildNodes: NodeList = svgElement.childNodes;

        currentChildNodes.forEach(node => svgElement.removeChild(node));
        newChildNodes.forEach(node => svgElement.appendChild(node));

        const iconXmlns: string = newIconElement.getAttribute('xmlns') || 'http://www.w3.org/2000/svg';
        this.renderer.setAttribute(svgElement, 'xmlns', iconXmlns);

        const iconViewBox: string = newIconElement.getAttribute('viewBox') || '0 0 32 32';
        this.renderer.setAttribute(svgElement, 'viewBox', iconViewBox);
    }

    private getIconSizeClass(): string {
        let iconClass = 'sh-icon';

        if (this.iconSize && this.iconSize !== IconSize.REGULAR) {
            iconClass = `sh-icon--${this.iconSize}`;
        }

        return iconClass;
    }

    private removeAllDefaultClasses(element: SVGElement): void {
        this.renderer.removeClass(element, 'sh-icon');
        this.renderer.removeClass(element, `sh-icon--${IconSize.LARGE}`);
        this.renderer.removeClass(element, `sh-icon--${IconSize.SMALL}`);
    }
}
